import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import moment from "moment";
import TextInput from "../../../../common/TextInput";
import AccordionComponent from "../../../../common/Accordion";
import {
  fetchPickListByGroupName,
  fetchCompanyIDList,
  fetchUserTypeBaseOnWebRole,
  fetchDepartmenteBaseOnWebRole,
  addUser,
  fetcheaderinfo,
  fetcdeviceinfo,
  updateUser,
  validateUserPrefix,
} from "../../../../services/UserRegistrationApi";
import SearchableSingleSelectDropdown from "../../../../common/SearchableDropdown";
import SearchableMultiSelectDropdown from "../../../../common/SearchableMultiSelectDropdown";
import validateUserForm from "../actions/ValidateUserForm";
import DataTable from "../../../../common/DataTable";
import { LOGIN_DEVICE_HEADERS } from "../actions/Constant";
import Snackbar from "../../../../common/Snackbar";
import TextInputTypeField from "../../../../common/TextInputTypeField";
import { PICKLIST_GROUP_NAME, WEB_ROLE } from "../../../../common/Constant";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Button, Modal } from "react-bootstrap";
import Loader from "../../../../common/Loader";
import FormattedPhoneInput from "../../../../common/FormattedPhoneInput";
import { fetchNotificationCount } from "../../../../redux/slice/NotificationSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../redux/Store";

// Define the interface for AddNewUserProps
interface AddNewUserProps {
  userData: any;
  action: string;
  onCancel: (action: string) => void;
  loggedInUserid: any;
}

const currencyData = [
  {
    label: "USD",
    value: "USD",
  },
  {
    label: "INR",
    value: "INR",
  },
];

const AddNewUser: React.FC<AddNewUserProps> = ({
  userData,
  action,
  onCancel,
  loggedInUserid,
}) => {
  const isEditable = action !== "View";
  const [loading, setLoading] = useState(false);
  const [salutationOptions, setSalutationOptions] = useState<any[]>([]);
  const [isShortCodeValid, setIsShortCodeValid] = useState<boolean | null>(
    null,
  );
  const [genderOptions, setGenderOptions] = useState<any[]>([]);
  const [companyIDOptions, setCompanyIDOptions] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [userTypeOptions, setUserTypeOptions] = useState<any[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<any[]>(currencyData);
  const [webRoleOptions, setWebRoleOptions] = useState<any[]>([]);
  const [result, setResult] = useState<any>({});
  const [deviceInfoResult, setDeviceInfoResult] = useState<any>({});
  // State for snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const params = {
    gender: {
      groupName: PICKLIST_GROUP_NAME.Gender,
      triggerById: loggedInUserid,
    },
    salutation: {
      groupName: PICKLIST_GROUP_NAME.Salutation_Type,
      triggerById: loggedInUserid,
    },
    userType: {
      groupName: PICKLIST_GROUP_NAME.User_Types,
      triggerById: loggedInUserid,
    },
    department: {
      groupName: PICKLIST_GROUP_NAME.Organization_Departments,
      triggerById: loggedInUserid,
    },
    webRole: {
      groupName: PICKLIST_GROUP_NAME.Web_Application_Role,
      triggerById: loggedInUserid,
    },
  };

  const fullName = userData?.userfullname;

  const fetchData = async () => {
    try {
      setLoading(true);
      // Initialize an array for Promise.all
      const promises = [
        fetchPickListByGroupName(params.salutation),
        fetchPickListByGroupName(params.gender),
        fetchCompanyIDList(),
        fetchPickListByGroupName(params.department),
        fetchPickListByGroupName(params.userType),
        fetchPickListByGroupName(params.webRole),
      ];

      // Conditionally add the `fetcheaderinfo` call if `userData?.userId` is present
      if (userData?.userId) {
        promises.push(fetcheaderinfo(userData.userId));
      }

      const [
        salutationData,
        genderData,
        companyIDData,
        departmentData,
        userTypeData,
        webTypeData,
        userHeader,
      ] = await Promise.all(promises);

      setSalutationOptions(salutationData || []);
      setGenderOptions(genderData || []);
      setCompanyIDOptions(companyIDData || []);
      setDepartmentOptions(departmentData || []);
      setUserTypeOptions(userTypeData || []);
      setWebRoleOptions(webTypeData || []);

      if (userHeader) {
        setResult(userHeader || {});

        if (userHeader?.useroleids) {
          const roleIds = userHeader.useroleids
            .split(",")
            .map((id: string) => parseInt(id.trim(), 10));

          // Update `webRoleOptions` based on the selected roles
          setWebRoleOptions((prevOptions) => {
            const updatedOptions = updateWebRoleOptions(roleIds, prevOptions);
            return updatedOptions;
          });

          // Fetch department and user type based on the selected roles
          const [departmentId, usertypeId] = await Promise.all([
            fetchDepartmenteBaseOnWebRole(roleIds, loggedInUserid),
            fetchUserTypeBaseOnWebRole(roleIds, loggedInUserid),
          ]);

          setDepartmentOptions(departmentId || []);
          setUserTypeOptions(usertypeId || []);
        }
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceInfoData = async () => {
    try {
      const deviceInfo = await fetcdeviceinfo(userData.userId);
      const formattedDeviceInfo = deviceInfo.map((item: any) => ({
        ...item,
        lastlogindatetime: moment(item.lastlogindatetime).format(
          "MM/DD/YYYY | hh:mm A",
        ),
      }));
      setDeviceInfoResult(formattedDeviceInfo);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
    if (userData?.userId) {
      fetchDeviceInfoData();
    }
  }, []);

  const updateWebRoleOptions = (selectedRoleIds: number[], options: any[]) => {
    // Extract selected labels
    const selectedLabels = options
      .filter((option) => selectedRoleIds.includes(Number(option.value)))
      .map((option) => option.label);

    // Check if "App Admin" or "System Admin" is selected
    const isAppAdminSelected = selectedLabels.includes(WEB_ROLE.App_Admin);
    const isSystemAdminSelected = selectedLabels.includes(
      WEB_ROLE.System_Admin,
    );

    // Check if any other option is selected
    const isOtherRoleSelected = selectedLabels.some(
      (label) =>
        label !== WEB_ROLE.App_Admin && label !== WEB_ROLE.System_Admin,
    );

    // Update options based on selection
    const updatedOptions = options?.map((option) => {
      if (isAppAdminSelected || isSystemAdminSelected) {
        // If "App Admin" or "System Admin" is selected, disable all other options
        return {
          ...option,
          disabled: !selectedLabels.includes(option.label),
        };
      } else if (isOtherRoleSelected) {
        // If any other option is selected, disable "App Admin" and "System Admin"
        return {
          ...option,
          disabled: [WEB_ROLE.App_Admin, WEB_ROLE.System_Admin].includes(
            option.label,
          ),
        };
      } else {
        // If no options are selected, enable all options
        return {
          ...option,
          disabled: false,
        };
      }
    });

    return updatedOptions;
  };
  const handleShortCodeValidation = async (prefix: string) => {
    if (prefix.length < 4) {
      setIsShortCodeValid(null); // no validation yet
      return;
    }
    try {
      if (result.userPrefix == prefix) {
        return;
      } else {
        const exists = await validateUserPrefix(prefix);
        if (exists.responseCode == "0") {
          setIsShortCodeValid(false);
          setSnackbarMessage("User Prefix already exist");
          setError("error");
          setSnackbarOpen(true);
        } else {
          setIsShortCodeValid(true); // ✅ available
        }
      }
    } catch (error) {
      formik.setFieldError("ShortCode", "Error validating short code.");
      setIsShortCodeValid(false);
    }
  };
  const formik = useFormik({
    // Initial values for the form fields
    initialValues: {
      Userid: result?.userid || null,
      Salutationtypeid: result?.salutationtypeid || "",
      Firstname: result?.firstname || "",
      Middlename: result?.middlename || "",
      Lastname: result?.lastname || "",
      ShortCode: result?.userPrefix || "",
      Username: result?.username || "",
      Countersign: result?.countersign || "",
      Emailid: result?.emailid || "",
      Phonenumber: result?.phonenumber || "",
      Faxnumber: result?.faxnumber || "",
      Companyid: userData?.userId
        ? result?.companyid
        : companyIDOptions[0]?.value || "",
      Usertypeid: result?.usertypeid || "",
      Departmentid: result?.departmentid || "",
      Roleids: result?.useroleids
        ? result.useroleids
            .split(",")
            .map((id: string) => parseInt(id.trim(), 10))
        : [],
      Isactive: result?.isactive !== undefined ? result.isactive === 1 : true,
      Ismfa: result?.ismfa !== undefined ? result.ismfa === 1 : true,
      Currencytypeid: result?.currencyType || "USD",
      LastPasswordResetDate: result?.lastPasswordResetDate
        ? moment(result.lastPasswordResetDate).format("MM/DD/YYYY  hh:mm A")
        : null,
      UserRegistrationDate: result?.registrationdate
        ? moment(result.registrationdate).format("MM/DD/YYYY  hh:mm A")
        : null,
      LastMfaActivationDate:
        result?.ismfa === 1 && result?.lastmfaactiviationdate
          ? moment(result.lastmfaactiviationdate).format("MM/DD/YYYY  hh:mm A")
          : null,
      IsAccountVerified:
        result?.isaccountverified !== undefined
          ? result.isaccountverified === 1
          : true,
      AccountVerificationDate:
        result?.isaccountverified === 1 && result?.accountverificationdate
          ? moment(result.accountverificationdate).format("MM/DD/YYYY  hh:mm A")
          : null,
      LastMFADeActivationDate:
        result?.ismfa === 1 && result?.lastmfadeactivationdate
          ? moment(result.lastmfadeactivationdate).format("MM/DD/YYYY  hh:mm A")
          : null,
      IsFirstTimeLogin:
        result?.isfirsttimelogin !== undefined
          ? result.isfirsttimelogin === 1
          : true,
      FirstTimeLoginDate: result?.firsttimelogindate
        ? moment(result.firsttimelogindate).format("MM/DD/YYYY  hh:mm A")
        : null,
      PasswordExpiredDate: result?.passwordexpirydate
        ? moment(result.passwordexpirydate).format("MM/DD/YYYY")
        : null,
      IsLockUser:
        result?.islockuser !== undefined ? result.islockuser === 1 : true,
      LastLockUserDate:
        result?.islockuser === 1 && result?.lastuserlockdate
          ? moment(result.lastuserlockdate).format("MM/DD/YYYY  hh:mm A")
          : null,
      Isforcepasswordexpiry:
        result?.isforcepasswordexpiry !== undefined
          ? result.isforcepasswordexpiry === 1
          : true,
      LastForcepasswordexpiryDate:
        result.isforcepasswordexpiry === 1 &&
        result?.lastforcepasswordexpiredate
          ? moment(result.lastforcepasswordexpiredate).format(
              "MM/DD/YYYY  hh:mm A",
            )
          : null,
      LastSuccessfullLoginDate: result?.lastsuccessfulllogin
        ? moment(result.lastsuccessfulllogin).format("MM/DD/YYYY  hh:mm A")
        : null,
    },
    enableReinitialize: true, // Reinitialize if `result` data changes
    onSubmit: async (values) => {
      const newError = await validateUserForm(values);
      // Check if there are any errors
      if (newError && Object.keys(newError).length > 0) {
        const errorFields = Object.values(newError);
        // Combine error messages into a single string separated by newlines or commas
        const errorMessage = errorFields.join("\n");
        setSnackbarMessage(errorMessage);
        setError("error");
        setSnackbarOpen(true);
      } else {
        try {
          setLoading(true);

          const payload = {
            Salutationtypeid: values.Salutationtypeid,
            Firstname: values.Firstname?.trim(),
            Middlename: values.Middlename?.trim(),
            Lastname: values.Lastname?.trim(),
            Username: values.Username?.trim(),
            Emailid: values.Emailid?.trim(),
            Phonenumber: values.Phonenumber?.trim(),
            Faxnumber: values.Faxnumber?.trim(),
            Companyid: values.Companyid,
            Usertypeid: values.Usertypeid,
            Departmentid: values.Departmentid,
            Roleids: values.Roleids,
            Isactive: values.Isactive ? 1 : 0,
            Ismfa: values.Ismfa ? 1 : 0,
            LoggedInUserid: loggedInUserid,
            UserPrefix: values.ShortCode,
            currencyType: values.Currencytypeid,
          };

          const handleResponse = (
            response: any,
            successMessage: any,
            errorMessage: any,
          ) => {
            if (response && response?.isSuccess) {
              setSnackbarMessage(successMessage);
              setError("success");
              setSnackbarOpen(true);
              setTimeout(() => {
                onCancel("Submit");
              }, 500);
              dispatch(fetchNotificationCount(loggedInUserid));
            } else {
              setSnackbarMessage(errorMessage);
              setError("error");
              setSnackbarOpen(true);
            }
          };

          let response;
          if (result.userPrefix != values.ShortCode) {
            const exists = await validateUserPrefix(values.ShortCode);
            if (values.ShortCode.length == 4) {
              if (exists.responseCode == "0") {
                setSnackbarMessage("User Prefix already exist");
                setError("error");
                setSnackbarOpen(true);
                setLoading(false);
                return;
              }
            } else {
              setSnackbarMessage("User Prefix length shoud be 4");
              setError("error");
              setSnackbarOpen(true);
              setLoading(false);
              return;
            }
          }
          if (result?.userid) {
            // Update User
            response = await updateUser({
              ...payload,
              UserId: result.userid,
              IsLockUser: values.IsLockUser ? 1 : 0,
              Isforcepasswordexpiry: values.Isforcepasswordexpiry ? 1 : 0,
            });
            handleResponse(
              response,
              "User updated successfully.",
              response?.message,
            );
          } else {
            // Add New User
            response = await addUser({
              ...payload,
              Countersign: values.Countersign?.trim(),
            });
            handleResponse(
              response,
              "User created successfully.",
              response?.message,
            );
          }
        } catch (error) {
          console.error("Error during API call:", error);
        } finally {
          setLoading(false);
        }
      }
    },
  });

  // Fetches and updates user type and department based on the selected web role.
  const handleWebRoleChange = async (event: any) => {
    try {
      const selectedValues = event.target ? event.target.value : event;
      const roleIds = Array.isArray(selectedValues)
        ? selectedValues.map(Number)
        : [Number(selectedValues)];

      setWebRoleOptions((prevOptions) => {
        const updatedOptions = updateWebRoleOptions(roleIds, prevOptions);
        return updatedOptions;
      });
      // Fetch department and user type in parallel
      if (roleIds?.length > 0) {
        const [departmentId, usertypeId] = await Promise.all([
          fetchDepartmenteBaseOnWebRole(roleIds, loggedInUserid),
          fetchUserTypeBaseOnWebRole(roleIds, loggedInUserid),
        ]);

        setDepartmentOptions(departmentId || []);
        setUserTypeOptions(usertypeId || []);

        // Clear previously selected values
        formik.setFieldValue("Departmentid", []);
        formik.setFieldValue("Usertypeid", []);
      } else {
        setDepartmentOptions([]);
        setUserTypeOptions([]);
      }
    } catch (error) {
      console.error("Error handling web role change:", error);
    }
  };

  const resetWebRoleOptions = () => {
    const initialOptions = webRoleOptions.map((option) => ({
      ...option,
      disabled: false, // Enable all options
    }));
    setWebRoleOptions(initialOptions);
  };

  // Resets the form
  const handleCancel = () => {
    setShowModal(true);
  };

  // Resets the form and triggers the onCancel callback.
  const handleBack = () => {
    formik.resetForm();
    onCancel("Cancel");
  };

  const handleConfirmCancel = () => {
    formik.resetForm();
    if (!result?.userid) resetWebRoleOptions();
    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  //User Registration Accordion added here//

  const [userDetailFormKey, setuserDetailFormKey] = useState<string | null>(
    "0",
  );

  const userdetailformAccordion = (
    items: { title: string; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
        title:
          userDetailFormKey === index.toString() ? (
            item.title
          ) : (
            <>
              {item.title}
              <span className="fw-normal">
                {fullName ? ` - ${fullName}` : ""}
              </span>
            </>
          ),
      }))}
      activeKey={userDetailFormKey}
      onSelect={(key) => setuserDetailFormKey(key as string)}
    />
  );

  const [assignmentDetailKey, setassignmentDetailKey] = useState<string | null>(
    "0",
  );

  const assignmentdetailAccordion = (
    items: { title: string; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={assignmentDetailKey}
      onSelect={(key) => setassignmentDetailKey(key as string)}
    />
  );

  const [accountInfoKey, setaccountInfoKey] = useState<string | null>(null);

  const accountInfoAccordion = (
    items: { title: string; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={accountInfoKey}
      onSelect={(key) => setaccountInfoKey(key as string)}
    />
  );

  const [loginDevicesKey, setloginDevicesKey] = useState<string | null>(null);

  const loginDevicesAccordion = (
    items: { title: string; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={loginDevicesKey}
      onSelect={(key) => setloginDevicesKey(key as string)}
    />
  );

  return (
    <>
      <div className="main_wrapper_section">
        <Loader isLoading={loading} />
        <Container fluid className="px-5 accountinfo mt-3">
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            {userdetailformAccordion([
              {
                title: "User Details",
                content: (
                  <>
                    <Row className="row mb-3">
                      <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="selection_dropdown adj_label_lt"
                      >
                        <SearchableSingleSelectDropdown
                          label="Salutation"
                          value={formik.values.Salutationtypeid}
                          options={salutationOptions}
                          onChange={formik.handleChange}
                          name="Salutationtypeid"
                          required={true}
                          disabled={!isEditable}
                        />
                      </Col>
                      <Col xs={12} sm={6} lg={3}>
                        <TextInput
                          label="First Name"
                          name="Firstname"
                          value={formik.values.Firstname}
                          onChange={async (e) => {
                            let onlyText = e.target.value.replace(
                              /[^a-zA-Z]/g,
                              "",
                            );
                            formik.setFieldValue("Firstname", onlyText);

                            const firstTwo = onlyText.slice(0, 2);
                            const lastTwo =
                              formik.values.Lastname?.slice(0, 2) || "";
                            const prefix = (firstTwo + lastTwo).toUpperCase();
                            formik.setFieldValue("ShortCode", prefix);
                            await handleShortCodeValidation(prefix);
                          }}
                          spacesTrim={true}
                          required={true}
                          maxLength={30}
                          disabled={!isEditable}
                        />
                      </Col>
                      <Col xs={12} sm={6} lg={3}>
                        <TextInput
                          label="Middle Name"
                          name="Middlename"
                          value={formik.values.Middlename}
                          onChange={(e) => {
                            let onlyText = e.target.value.replace(
                              /[^a-zA-Z]/g,
                              "",
                            );
                            formik.setFieldValue("Middlename", onlyText);
                          }}
                          spacesTrim={true}
                          maxLength={20}
                          disabled={!isEditable}
                        />
                      </Col>
                      <Col xs={12} sm={6} lg={3}>
                        <TextInput
                          label="Last Name"
                          name="Lastname"
                          value={formik.values.Lastname}
                          onChange={async (e) => {
                            let onlyText = e.target.value.replace(
                              /[^a-zA-Z]/g,
                              "",
                            );
                            formik.setFieldValue("Lastname", onlyText);

                            const firstTwo =
                              formik.values.Firstname?.slice(0, 2) || "";
                            const lastTwo = onlyText.slice(0, 2);
                            const prefix = (firstTwo + lastTwo).toUpperCase();
                            formik.setFieldValue("ShortCode", prefix);
                            await handleShortCodeValidation(prefix);
                          }}
                          spacesTrim={true}
                          required={true}
                          maxLength={30}
                          disabled={!isEditable}
                        />
                      </Col>
                    </Row>

                    <Row className="row mb-3">
                      <Col xs={12} sm={6} lg={3}>
                        <TextInput
                          label="Username"
                          name="Username"
                          required={true}
                          value={formik.values.Username}
                          onChange={(e) => {
                            let onlyText = e.target.value.replace(
                              /[^a-zA-Z0-9@._]/g,
                              "",
                            );
                            formik.setFieldValue("Username", onlyText);
                          }}
                          spacesTrim={true}
                          maxLength={50}
                          disabled={!isEditable}
                          autoComplete="off"
                        />
                      </Col>
                      <Col xs={12} sm={6} lg={3}>
                        <TextInputTypeField
                          label="Password"
                          name="Countersign"
                          value={formik.values.Countersign}
                          onChange={(e) => {
                            const trimmedValue = e.target.value.trimStart();
                            if (trimmedValue !== "") {
                              formik.setFieldValue("Countersign", trimmedValue);
                            } else {
                              formik.setFieldValue("Countersign", "");
                            }
                          }}
                          required={true}
                          type="password"
                          maxLength={30}
                          spacesTrim={true}
                          disabled
                        />
                      </Col>
                      <Col xs={3}>
                        <div style={{ position: "relative" }}>
                          <TextInput
                            label="User Prefix"
                            name="ShortCode"
                            value={formik.values.ShortCode}
                            required
                            maxLength={4}
                            onChange={async (e) => {
                              let onlyText = e.target.value
                                .replace(/[^a-zA-Z]/g, "")
                                .toUpperCase();
                              formik.setFieldValue("ShortCode", onlyText);
                              await handleShortCodeValidation(onlyText);
                            }}
                          />
                          {isShortCodeValid == true && (
                            <span className="material-symbols-outlined verfied_arrow">
                              verified
                            </span>
                          )}
                        </div>
                      </Col>
                      <Col xs={12} sm={6} lg={3}>
                        <TextInput
                          label="Email ID"
                          className="emailHyperLink"
                          name="Emailid"
                          value={formik.values.Emailid}  
                            onClick={() => {
    if (result?.userid) {
      window.location.href = `mailto:${formik.values.Emailid}`;
    }
  }}
                          onChange={(e) => {
                            const trimmedValue = e.target.value.trimStart();
                            if (trimmedValue !== "") {
                              formik.setFieldValue("Emailid", trimmedValue);
                            } else {
                              formik.setFieldValue("Emailid", "");
                            }
                          }}
                          
                          spacesTrim={true}
                          required={true}
                          maxLength={100}
                          disabled={!isEditable}
                        />
                       
                      </Col>
                    </Row>

                    <Row className="row  mt-3">
                      <Col xs={12} sm={6} lg={3}>
                        <FormattedPhoneInput
                          label="Phone Number"
                          name="Phonenumber"
                          value={formik.values.Phonenumber}
                          onChange={(value) =>
                            formik.setFieldValue("Phonenumber", value)
                          }
                          disabled={!isEditable}
                          required={true}
                        />
                      </Col>
                      <Col xs={12} sm={6} lg={3}>
                        <FormattedPhoneInput
                          label="Fax No"
                          name="Faxnumber"
                          value={formik.values.Faxnumber}
                          onChange={(value) =>
                            formik.setFieldValue("Faxnumber", value)
                          }
                          disabled={!isEditable}
                        />
                      </Col>
                      <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="selection_dropdown adj_label_lt"
                      >
                        <SearchableSingleSelectDropdown
                          label="Currency Type"
                          value={formik.values.Currencytypeid}
                          options={currencyOptions}
                          onChange={formik.handleChange}
                          name="Currencytypeid"
                          required={true}
                          disabled={!isEditable}
                        />
                      </Col>
                    </Row>
                  </>
                ),
              },
            ])}

            {assignmentdetailAccordion([
              {
                title: "Assignment Details",
                content: (
                  <>
                    <Row>
                      <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="col-md-3 selection_dropdown adj_label_lt"
                      >
                        <SearchableSingleSelectDropdown
                          label="Company ID"
                          name="Companyid"
                          options={companyIDOptions}
                          value={formik.values.Companyid}
                          onChange={formik.handleChange}
                          required={true}
                          disabled={!isEditable}
                        />
                      </Col>
                      <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="col-md-3 selection_dropdown adj_label_lt"
                      >
                        <SearchableMultiSelectDropdown
                          label="Web Role"
                          name="Roleids"
                          options={webRoleOptions}
                          values={formik.values.Roleids}
                          onChange={(e) => {
                            formik.handleChange(e);
                            handleWebRoleChange(e);
                          }}
                          disabled={!isEditable}
                          required={true}
                        />
                      </Col>

                      <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="col-md-3 selection_dropdown adj_label_lt"
                      >
                        <SearchableSingleSelectDropdown
                          label="Department"
                          name="Departmentid"
                          options={departmentOptions}
                          value={formik.values.Departmentid}
                          onChange={formik.handleChange}
                          required={true}
                          disabled={
                            !isEditable ||
                            !formik.values.Roleids ||
                            formik.values.Roleids?.length == 0
                          }
                        />
                      </Col>

                      <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="col-md-3 selection_dropdown adj_label_lt"
                      >
                        <SearchableSingleSelectDropdown
                          label="User Type"
                          name="Usertypeid"
                          options={userTypeOptions}
                          value={formik.values.Usertypeid}
                          onChange={formik.handleChange}
                          required={true}
                          disabled={
                            !isEditable ||
                            !formik.values.Roleids ||
                            formik.values.Roleids?.length == 0
                          }
                        />
                      </Col>
                    </Row>
                  </>
                ),
              },
            ])}

            {userData?.userId && action !== "Add" ? (
              <>
                {accountInfoAccordion([
                  {
                    title: "Account Information",
                    content: (
                      <>
                        <Row className="row mb-3">
                          <Col xs={6}>
                            <TextInput
                              label="User Registration Date"
                              name="UserRegistrationDate"
                              value={formik.values.UserRegistrationDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                          <Col xs={2} className="d-flex align-items-center">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="Ismfa"
                                name="Ismfa"
                                checked={formik.values.Ismfa}
                                onChange={formik.handleChange}
                                style={{ cursor: "pointer" }}
                                disabled={!isEditable}
                              />
                              <label className="form-check-label">
                                Is MFA?
                              </label>
                            </div>
                          </Col>
                          <Col xs={4}>
                            <TextInput
                              label="Last MFA Activation Date"
                              name="LastMfaActivationDate"
                              value={formik.values.LastMfaActivationDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                        </Row>
                        <Row className="row mb-3">
                          <Col xs={2} className="d-flex align-items-center">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="IsAccountVerified"
                                name="IsAccountVerified"
                                checked={formik.values.IsAccountVerified}
                                onChange={formik.handleChange}
                                style={{ cursor: "pointer" }}
                                disabled={true}
                              />
                              <label className="form-check-label">
                                Is Account Verified?
                              </label>
                            </div>
                          </Col>
                          <Col xs={4}>
                            <TextInput
                              label="Account Verification Date"
                              name="AccountVerificationDate"
                              value={formik.values.AccountVerificationDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                          <Col xs={2}></Col>
                          <Col xs={4}>
                            <TextInput
                              label="Last MFA De-Activation Date"
                              name="LastMFADeActivationDate"
                              value={formik.values.LastMFADeActivationDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                        </Row>
                        <Row className="row mb-3">
                          <Col xs={2} className="d-flex align-items-center">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="IsFirstTimeLogin"
                                name="IsFirstTimeLogin"
                                checked={formik.values.IsFirstTimeLogin}
                                onChange={formik.handleChange}
                                style={{ cursor: "pointer" }}
                                disabled={true}
                              />
                              <label className="form-check-label">
                                Is First Time Login?
                              </label>
                            </div>
                          </Col>
                          <Col xs={4}>
                            <TextInput
                              label="First Time Login Date"
                              name="FirstTimeLoginDate"
                              value={formik.values.FirstTimeLoginDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                          <Col xs={2}></Col>
                          <Col xs={4}>
                            <TextInput
                              label="Password Expiry Date"
                              name="PasswordExpiredDate"
                              value={formik.values.PasswordExpiredDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                        </Row>
                        <Row className="row mb-3">
                          <Col xs={2} className="d-flex align-items-center">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="IsLockUser"
                                name="IsLockUser"
                                checked={formik.values.IsLockUser}
                                onChange={formik.handleChange}
                                style={{ cursor: "pointer" }}
                                disabled={!isEditable}
                              />
                              <label className="form-check-label">
                                Lock User?
                              </label>
                            </div>
                          </Col>
                          <Col xs={4}>
                            <TextInput
                              label="Last User Lock Date"
                              name="LastLockUserDate"
                              value={formik.values.LastLockUserDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                          <Col xs={2} className="d-flex align-items-center">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="Isforcepasswordexpiry"
                                name="Isforcepasswordexpiry"
                                checked={formik.values.Isforcepasswordexpiry}
                                onChange={formik.handleChange}
                                style={{ cursor: "pointer" }}
                                disabled={!isEditable}
                              />
                              <label className="form-check-label">
                                Force Password Expiry
                              </label>
                            </div>
                          </Col>
                          <Col xs={4}>
                            <TextInput
                              label="Last Forced Password Expiry Date"
                              name="LastForcepasswordexpiryDate"
                              value={formik.values.LastForcepasswordexpiryDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                        </Row>
                        <Row className="row mb-3">
                          <Col xs={8}>
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="Isactive"
                                name="Isactive"
                                checked={formik.values.Isactive}
                                onChange={formik.handleChange}
                                style={{ cursor: "pointer" }}
                                disabled={!isEditable}
                              />
                              <label className="form-check-label">
                                Is Active?
                              </label>
                            </div>
                          </Col>

                          <Col xs={4}>
                            <TextInput
                              label="Last Successfull Login Date"
                              name="LastSuccessfullLoginDate"
                              value={formik.values.LastSuccessfullLoginDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                          <Row className="row mb-3"></Row>
                          <Col xs={8}></Col>
                          <Col xs={4}>
                            <TextInput
                              label="Last Password Reset Date"
                              name="LastPasswordResetDate"
                              value={formik.values.LastPasswordResetDate}
                              onChange={formik.handleChange}
                              disabled={true}
                            />
                          </Col>
                        </Row>
                      </>
                    ),
                  },
                ])}

                {loginDevicesAccordion([
                  {
                    title: "Login Devices",
                    content: (
                      <>
                        <DataTable
                          headers={LOGIN_DEVICE_HEADERS}
                          rows={deviceInfoResult}
                          isLogTable={true}
                        />
                      </>
                    ),
                  },
                ])}
              </>
            ) : (
              ""
            )}

            <div className="row mb-2 mt-1">
              <div className="col-md-6 d-flex justify-content-start mb-2">
                {/* New Checkboxes */}
                {!userData?.userId && action == "Add" ? (
                  <>
                    <div className="form-check p-2 py-2 me-5 m">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="Isactive"
                        name="Isactive"
                        checked={formik.values.Isactive}
                        onChange={formik.handleChange}
                        style={{ cursor: "pointer" }}
                        disabled={!isEditable}
                      />
                      <label className="form-check-label">Is Active?</label>
                    </div>

                    <div className="form-check p-2 py-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="Ismfa"
                        name="Ismfa"
                        checked={formik.values.Ismfa}
                        onChange={formik.handleChange}
                        style={{ cursor: "pointer" }}
                        disabled={!isEditable}
                      />
                      <label className="form-check-label">Is MFA?</label>
                    </div>
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-6 d-flex justify-content-start mb-2">
                {" "}
                <button
                  type="button"
                  className="btn d-flex align-items-center btn-text-border"
                  onClick={handleBack}
                >
                  <span className="material-icons mr-2">arrow_back</span>Back
                </button>
              </div>
              <div className="col-md-6 d-flex justify-content-end mb-2">
                <button
                  type="button"
                  className="btn d-flex align-items-center btn-text me-3"
                  onClick={handleCancel}
                  disabled={!isEditable || !formik.dirty}
                >
                  <span className="material-icons mr-2">close</span>Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary ml-3 d-flex align-items-center"
                  disabled={!isEditable || !formik.dirty || formik.isSubmitting}
                >
                  <span className="material-icons mx-1">save</span>Save
                </button>
              </div>
            </div>
          </form>
          <Modal show={showModal} onHide={handleCloseModal}>
            {/* <Modal.Header closeButton>
              <Modal.Title>Confirm Cancellation</Modal.Title>
            </Modal.Header> */}
            <Modal.Body className="fw-bold text-left modelheading ">
              <div> Are you sure you want to cancel the Update?</div>
              <div style={{ fontWeight: "300" }}>
                {" "}
                Any unsaved data will be lost.
              </div>
            </Modal.Body>
            <Modal.Footer className="border-0 d-flex justify-content-end">
              <Button
                variant="outline-info"
                className="d-flex align-items-center"
                onClick={handleCloseModal}
              >
                <span className="material-icons mr-2">close</span> Cancel
              </Button>
              <Button
                variant="primary"
                className="ms-3 d-flex align-items-center"
                onClick={handleConfirmCancel}
              >
                <span className="material-icons mx-1">check</span> Confirm
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
        <Snackbar
          open={snackbarOpen}
          message={snackbarMessage}
          onClose={() => setSnackbarOpen(false)}
          severity={error}
        />
      </div>
    </>
  );
};

export default AddNewUser;
